import { getInstance } from '../../../scripts/lib/views.js';
import * as Sentry from '@sentry/browser';
import FormFile  from '../form-file/form-file';

const name = 'form';

export default class Form {

  constructor ($el) {
    this.$el = $el;
    this.action = $el.action;
    this.loading = false;
    this.conditionalFields = [];
    this.googleAnalyticsActive = false;
    this.rtlGoogleAnalyticsActive = $el.dataset.rtlTracked === '1';
    this.fbPixelActive = false;

    // Handle inactive forms
    if ($el.classList.contains(`${name}--inactive`)) {
      return;
    }

    // Bind elements
    this.$fields =
      [...$el.querySelectorAll(
        '.form-field:not(.form-field--list, .field-phone__phone, .field-phone__hidden), .form-checkbox, .form-phone, .form-list, .form-file, .form-newsletter')
      ];
    this.$button = $el.querySelector(`.${name}__button-submit`);
    this.$alert = $el.querySelector(`.${name}__alert`);
    this.$overlaySuccess = $el.querySelector(`.${name}__overlay-success`);
    this.$overlayFailure = $el.querySelector(`.${name}__overlay-failure`);

    // Read form data
    const $header = $el.querySelector(`.${name}__header`);
    this.header = $header !== null ? JSON.parse($header.innerHTML) : {};

    // Retrieve instances
    this.fields = this.$fields
      .map($field => {
        const field = getInstance($field);
        field.setDelegate(this);

        return field;
      });

    // Force field value updates to initialize conditional fields after loading all fields
    this.fields.forEach(field => this.fieldValueDidChange(field));

    // Bind events
    $el.onsubmit = evt => {
      // Prevent form element from submitting
      evt.preventDefault();
      // Trigger submit
      this.submit();
    };

    // Update submit button initially and get the form nonce
    this.updatesSubmitButton();
    this.initFormHeader();
  }

  initFormHeader () {
    let that = this;

    fetch(this.$el.dataset.formHeaderUrl, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({
        signature: this.$el.dataset.nonceSignature
      })
    }).then((response) => {
      if (!response || !response.ok) {
        this.logSubmissionToSentry(response);
      }

      return response.json();
    }).then((result) => {

      if (typeof result.nonce !== 'undefined') {
        that.header.nonce = result.nonce;
      }

      if (typeof result.submission_id !== 'undefined') {
        that.header.submission_id = result.submission_id;
      }

      // Provide header values for fields where needed.
      this.fields.forEach(field => this.provideHeaderValues(field, this.header));

      this.updatesSubmitButton();

    }).catch((error) => {
      Sentry.captureException(error);
    });
  }

  /**
   * Fetch the current cookie-consent status and update the variables accordingly.
   */

  updateCookieConsentStatus() {

    const manager = window.klaro.getManager();

    this.googleAnalyticsActive = manager.getConsent('google-analytics');
    this.fbPixelActive = manager.getConsent('facebook-pixel');

  }


  /**
   * Let fields register themselves as conditional fields so
   * field changes will trigger updating of these fields
   *
   * @param field
   * @param targetField
   */
  registerConditionalField (field, targetField) {
    if (typeof this.conditionalFields[targetField] === 'undefined') {
      this.conditionalFields[targetField] = [];
    }

    this.conditionalFields[targetField].push(field);
  }

  /**
   * Called when the form should be submitted.
   */
  async submit () {
    // Ignore submission requests while a previous request is being completed
    if (this.loading) {
      return;
    }

    // Trigger field validation
    this.fields.forEach(field => field.validate());

    // Check if form is valid
    if (!this.isValid()) {
      return;
    }

    const fields = this.collectFormData();

    const data = {
      header: this.header,
      fields
    };

    this.setLoading(true);

    const response = await fetch(this.action, {
      method: 'POST',
      body: JSON.stringify(data)
    }).catch(response => {
      this.logSubmissionToSentry(response);
    });

    // Check response code
    if (response && response.ok) {

      // get current consent status
      this.updateCookieConsentStatus();

      // Show success overlay
      this.$overlaySuccess.classList.add('overlay--visible');

      // Fire the Google Analytics event on a successful submission
      if (this.googleAnalyticsActive && typeof ga !== 'undefined') {
        ga('send', 'event', 'Formular', this.header.type, this.header.subject); // eslint-disable-line

        if (this.rtlGoogleAnalyticsActive) {
          ga('rtl.send', 'event', 'Formular', this.header.type, this.header.subject); // eslint-disable-line
        }
      }

      // Fire the Facebook Pixel event on a successful submission
      if (this.fbPixelActive && this.header.type === 'Casting') {
        fbq('track', 'Lead'); // eslint-disable-line
      }

    } else {
      // Show failure overlay
      this.$overlayFailure.classList.add('overlay--visible');

      // Wait for the promise to resolve and log the error received from the backend to Sentry
      response && response.json().then((response) => {

        // Try to get a new nonce if the provided one is invalid
        // eslint-disable-next-line no-prototype-builtins
        if (response.hasOwnProperty('data') && response.data.message === 'Invalid nonce') {
          this.initFormHeader();
        }

        this.logSubmissionToSentry(response);
      });
    }

    this.setLoading(false);
  }

  /**
   * Returns wether form is valid.
   * @return {boolean}
   */
  isValid () {
    // This form is valid when all its fields are valid
    return this.fields.reduce((formValid, field) =>
      formValid && field.isValid(), true);
  }

  /**
   * This function distributes changes of a field to conditional fields
   *
   * @param field
   */
  fieldValueDidChange (field) {
    let fieldStatus;

    if (field.getValue() === true ||
      field.getValue().toString().toLowerCase().indexOf('ja') !== -1 ||
      field.getValue().toString().toLowerCase().indexOf('yes') !== -1
    ) {
      fieldStatus = 'checked';
    } else {
      fieldStatus = 'unchecked';
    }

    if (typeof this.conditionalFields[field.name] !== 'undefined') {

      this.conditionalFields[field.name].forEach(conditional => {
        conditional.toggleConditional(fieldStatus);
      });
    }

    // Update submit button
    this.updatesSubmitButton();
  }

  /**
   * Triggered when a field changes its valid state.
   * @param {object} field Sender
   * @param {boolean} valid
   */
  fieldValidDidChange (field, valid) {
    // Update submit button
    this.updatesSubmitButton();
    this.updatesAlertDisplay();
  }

  /**
   * Updates submit button disabled state.
   */
  updatesSubmitButton () {
    // Update button disable state
    this.$button.disabled = !this.header.nonce || !this.header.submission_id || !this.isValid() || this.loading;
  }

  updatesAlertDisplay () {
    let showAlert = !this.isValid();
    this.$alert.classList.toggle(`${name}__alert--visible`, showAlert);
  }

  /**
   * Updates the loading state.
   * @param {boolean} loading Wether the form is waiting for a reponse.
   */
  setLoading (loading) {
    if (this.loading !== loading) {
      this.loading = loading;
      this.updatesSubmitButton();
    }
  }

  /**
   * Return the form submission id
   * @return string
   */
  getSubmissionId () {
    return this.header.submission_id;
  }

  provideHeaderValues(field, header) {
    if (field instanceof FormFile) {
      field.setHeaderValues(header);
    }
  }

  /**
   * Send a form submission to Sentry for logging in case of an error
   * @param fetchResponse
   */
  logSubmissionToSentry (fetchResponse) {
    // Get field values
    const fields = {
      fieldValues: this.collectFormData(),
    };

    // Replace alphanumeric string values for privacy.
    //for (var property in fields.fieldValues) {
    //  if (typeof fields.fieldValues[property] === 'string') {
    //    var text = String(fields.fieldValues[property]);
    //    text = text.toLowerCase().replace(/[a-z]/g, 'a');
    //    text = text.replace(/[0-9]/g, '0');
    //    fields.fieldValues[property] = text;
    //  };
    //}
    const sentryHeader = {...fields, ...this.header };

    Sentry.withScope(scope => {
      // Set the form id as the fingerprint (serves as the form identifier)
      scope.setFingerprint([`form-${this.header.form_id}`]);
      // Set the submission id as the user
      scope.setUser({'id': this.getSubmissionId()});
      // Append the backend response to the error data
      scope.setExtra('response', fetchResponse);
      scope.setExtra('header', sentryHeader);
      scope.setLevel(Sentry.Severity.Error);
      // Send the error to Sentry
      Sentry.captureException(`Submission for form ${this.header.form_id} could not be sent at ${window.location.pathname} with header: ${JSON.stringify(this.header)}`);
    });
  }

  /**
   * Collect form data
   */
  collectFormData () {
    // Collect data
    const fields = {};
    this.fields.forEach(field => {
      // Use field names as keys to point to the value
      // Only add the field if it does not exist or if the value is null
      if (!fields[field.getName()] || fields[field.getName()] === null) {
        fields[field.getName()] = field.getValue();

        if (field.getValue().toString().indexOf('#SUBMISSION_ID#') === 0) {
          fields[field.getName()] = this.getSubmissionId();
        }
      }
    });

    return fields;
  }
}
